import { catalogCacheFallback, catalogCacheOnSuccess } from '../lib/catalog-offline-cache.js';
import type { ApiResponse, AuthSessionDto } from '@rimati/shared';

/**
 * Wrapper tipado sobre o fetch nativo (ADR-011). Nenhum componente React
 * conhece o formato bruto de resposta: o envelope e desembalado aqui.
 */
export const API_BASE = '/api/v1';

/** Um campo que o servidor recusou, com o motivo dele. */
export interface CampoInvalido {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * R101 - QUAL campo o servidor recusou.
   *
   * O servidor sempre mandou isto: `parseOrThrow` transforma cada problema do
   * schema em `{ path, message }`. O cliente jogava fora no construtor, e por
   * isso a unica coisa que a tela conseguia fazer com um erro de validacao era
   * mostrar um aviso flutuante generico.
   *
   * Sem o caminho do campo, "erro aponta campo" e impossivel — nao por decisao
   * de desenho, mas porque a informacao nao chegava. Ela chegava; era descartada
   * a um passo de quem precisava dela.
   */
  readonly fields: CampoInvalido[];

  /**
   * Quantos segundos o servidor pediu para esperar, quando pediu.
   *
   * Só vem no 429. É a diferença entre esperar o que foi pedido e chutar um
   * intervalo — chutar curto demais mantém o limite estourado, e longo demais
   * deixa a tela parada sem motivo.
   */
  readonly retryAfterSegundos: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    fields: CampoInvalido[] = [],
    retryAfterSegundos: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfterSegundos = retryAfterSegundos;
  }
}

/**
 * A mensagem para um campo, ou `null`.
 *
 * `null` e nao string vazia: um campo sem erro nao tem mensagem, e string vazia
 * faria a tela reservar espaco para um aviso que nao existe.
 *
 * Casa por PREFIXO porque o servidor descreve caminhos aninhados
 * (`fields.procedure`), e a tela costuma conhecer so o comeco.
 */
export function erroDoCampo(erro: unknown, campo: string): string | null {
  if (!(erro instanceof ApiError)) return null;
  const achado = erro.fields.find(
    (item) => item.path === campo || item.path.startsWith(`${campo}.`),
  );
  return achado?.message ?? null;
}

let accessToken: string | null = null;
let tokenGeneration = 0;
let contextGeneration = 0;
let contextChanging = false;
let onSessionRefresh: ((session: AuthSessionDto) => boolean) | null = null;
export function setSessionRefreshHandler(
  handler: ((session: AuthSessionDto) => boolean) | null,
): void {
  onSessionRefresh = handler;
}
export function beginCompanyTransition(): void {
  contextChanging = true;
  contextGeneration += 1;
}
export function endCompanyTransition(): void {
  contextChanging = false;
  contextGeneration += 1;
}
function contextChanged(): ApiError {
  return new ApiError(
    409,
    'SESSION_CONTEXT_CHANGED',
    'A empresa da sessão mudou. Atualize a página.',
  );
}
let refreshing: Promise<boolean> | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenGeneration += 1;
}

/** Uma renovação por rajada; nunca restaura sessão após sair/trocar de usuário. */
export async function refreshAccessToken(): Promise<boolean> {
  if (contextChanging) throw contextChanged();
  if (refreshing) return refreshing;
  const generation = tokenGeneration;
  const context = contextGeneration;
  refreshing = (async () => {
    let response: Response;
    try {
      response = await fetch(API_BASE + '/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        signal: AbortSignal.timeout(15000),
      });
    } catch {
      throw new ApiError(0, 'NETWORK_ERROR', 'Não conseguimos renovar a conexão. Tente novamente.');
    }
    const payload = await readJson<AuthSessionDto>(response);
    if (context !== contextGeneration || contextChanging) throw contextChanged();
    if (generation !== tokenGeneration) return false;
    if (!response.ok || !payload?.success || !payload.data.accessToken) {
      if (response.status === 401) onUnauthenticated?.();
      return false;
    }
    if (onSessionRefresh && !onSessionRefresh(payload.data)) throw contextChanged();
    setAccessToken(payload.data.accessToken);
    return true;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Read-only local epoch; contains no token or authority grant. */
export function getSessionContextStamp(): string {
  return String(tokenGeneration) + ':' + String(contextGeneration) + ':' + String(contextChanging);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  /** Public forms never send a management token or refresh its session. */
  anonymous?: boolean;
  signal?: AbortSignal;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Nao dispara o logout automatico em 401 (usado pelo proprio login). */
  skipAuthRedirect?: boolean;
  retriedAfterRefresh?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const context = contextGeneration;
  if (!options.anonymous && contextChanging && !path.startsWith('/auth/')) throw contextChanged();
  const url = new URL(API_BASE + path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  let response: Response;
  const requestSignal = options.signal ?? AbortSignal.timeout(60000);
  try {
    response = await fetch(url, {
      signal: requestSignal,
      method: options.method ?? 'GET',
      credentials: options.anonymous ? 'omit' : 'include',
      headers: {
        ...(options.body === undefined || options.body instanceof FormData
          ? {}
          : { 'content-type': 'application/json' }),
        ...(!options.anonymous && accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(options.body === undefined
        ? {}
        : { body: options.body instanceof FormData ? options.body : JSON.stringify(options.body) }),
    });
  } catch {
    if (requestSignal.aborted)
      throw new ApiError(
        0,
        requestSignal.reason?.name === 'TimeoutError' ? 'REQUEST_TIMEOUT' : 'REQUEST_ABORTED',
        'A espera terminou. Consulte o resultado para verificar se o processamento foi concluído.',
      );
    if (!options.anonymous) {
      const copia = catalogCacheFallback<T>(options.method ?? 'GET', path, options.body);
      if (copia !== null) return copia;
    }
    throw new ApiError(0, 'NETWORK_ERROR', 'Não conseguimos falar com o servidor.');
  }

  if (!options.anonymous && context !== contextGeneration) throw contextChanged();

  if (
    !options.anonymous &&
    response.status === 401 &&
    !options.skipAuthRedirect &&
    !options.retriedAfterRefresh &&
    !path.startsWith('/auth/')
  ) {
    if (await refreshAccessToken())
      return request<T>(path, { ...options, retriedAfterRefresh: true });
  }

  if (response.status === 204) return undefined as T;

  const payload = await readJson<T>(response);
  if (!options.anonymous && context !== contextGeneration) throw contextChanged();

  if (!response.ok || !payload?.success) {
    const error = payload && !payload.success ? payload.error : null;
    if (!options.anonymous && response.status === 401 && !options.skipAuthRedirect)
      onUnauthenticated?.();

    /*
      R069 - o `Retry-After` do 429 é LIDO, e não descartado.

      Ele é a única informação que transforma "tente de novo" em "tente de novo
      DAQUI A TANTO". Sem ele, quem repete escolhe um intervalo no escuro: curto
      demais mantém o limite estourado, longo demais deixa a tela parada à toa.
    */
    const pedido = Number(response.headers.get('retry-after'));

    throw new ApiError(
      response.status,
      error?.code ?? 'INTERNAL_ERROR',
      mensagemDoErro(payload, error),
      camposInvalidos(error),
      Number.isFinite(pedido) && pedido > 0 ? pedido : null,
    );
  }

  if (!options.anonymous)
    catalogCacheOnSuccess(options.method ?? 'GET', path, options.body, payload.data);
  return payload.data;
}

/**
 * Pede um arquivo binario (ex.: audio). Erros chegam como ApiError com a
 * mensagem da API (corpo JSON com `mensagem` ou `message`); sem corpo valido,
 * vale uma mensagem generica.
 */
export async function requestBlob(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal; retriedAfterRefresh?: boolean } = {},
): Promise<Blob> {
  const context = contextGeneration;
  if (contextChanging) throw contextChanged();
  const signal = options.signal ?? AbortSignal.timeout(60000);
  let response: Response;
  try {
    response = await fetch(new URL(API_BASE + path, window.location.origin), {
      signal,
      method: options.method ?? 'GET',
      credentials: 'include',
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Não conseguimos falar com o servidor.');
  }
  if (context !== contextGeneration) throw contextChanged();
  if (response.status === 401 && !options.retriedAfterRefresh) {
    if (await refreshAccessToken()) return requestBlob(path, { ...options, retriedAfterRefresh: true });
  }
  if (!response.ok) {
    const payload = await readJson<unknown>(response);
    const error = payload && !payload.success ? payload.error : null;
    throw new ApiError(
      response.status,
      error?.code ?? (payload as { codigo?: string } | null)?.codigo ?? 'INTERNAL_ERROR',
      mensagemDoErro(payload, error),
    );
  }
  return response.blob();
}

/**
 * Le `details` sem confiar no formato.
 *
 * O corpo vem da rede: um formato inesperado nao pode derrubar a tela que esta
 * tentando MOSTRAR um erro. O que nao tiver `path` e `message` de texto e
 * ignorado, e a tela cai no aviso geral.
 */
/**
 * Texto do erro para a pessoa. As rotas de voz respondem com `mensagem` (pt-BR);
 * o envelope padrão usa `message`. Vale o primeiro que for um texto não vazio.
 */
function mensagemDoErro(payload: unknown, error: unknown): string {
  const candidatos = [
    (payload as { mensagem?: unknown } | null)?.mensagem,
    (error as { mensagem?: unknown } | null)?.mensagem,
    (error as { message?: unknown } | null)?.message,
  ];
  const achado = candidatos.find((c): c is string => typeof c === 'string' && c.trim() !== '');
  return achado ?? 'Algo deu errado. Tente novamente.';
}

function camposInvalidos(error: { details?: unknown } | null): CampoInvalido[] {
  const detalhes = error?.details;
  if (!Array.isArray(detalhes)) return [];
  return detalhes.flatMap((item) => {
    if (typeof item !== 'object' || item === null) return [];
    const { path, message } = item as { path?: unknown; message?: unknown };
    return typeof path === 'string' && typeof message === 'string' ? [{ path, message }] : [];
  });
}

/** Resposta sem corpo JSON valido nao pode derrubar a interface. */
async function readJson<T>(response: Response): Promise<ApiResponse<T> | null> {
  try {
    return (await response.json()) as ApiResponse<T>;
  } catch {
    return null;
  }
}
